"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { loadStripe } from "@stripe/stripe-js"
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { ThemeProvider } from '@mui/material/styles'
import { Box, Typography, TextField, Button } from '@mui/material'
import theme from '../../theme'
import "./SubscriptionScreen.css"
import { useAuth } from "../../context/AuthContext"
import { useTranslation } from 'react-i18next'
import { track } from '../../third-party/MixPanel'

if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
    throw new Error('Missing Stripe publishable key')
}

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)

interface SubscriptionScreenProps {
    priceId: string
    onSuccess: () => void
    appearance?: {
        theme: string
        variables: {
            colorPrimary: string
            colorBackground: string
            colorText: string
        }
        rules: {
            '.Input': { border: string }
            '.Button': {
                backgroundColor: string
                color: string
                padding: string
                '&:hover': { backgroundColor: string }
            }
        }
    }
}

const SubscriptionForm: React.FC<SubscriptionScreenProps> = ({ priceId, onSuccess }) => {
    const stripe = useStripe()
    const elements = useElements()
    const { user } = useAuth()
    const { t } = useTranslation()
    const [email, setEmail] = useState(user?.username || "")
    const [isLoading, setIsLoading] = useState(false)
    const [message, setMessage] = useState("")
    console.log(user)
    console.log(priceId)

    // Track when payment form is opened
    useEffect(() => {
        track('payment_form_opened', {
            priceId: priceId,
            paymentMethod: 'stripe_card',
            hasEmail: !!email
        });
    }, []);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        if (!stripe || !elements) {
            return
        }

        if (!priceId) {
            setMessage(t('payment.errorInvalidPriceId'))
            return
        }

        track('payment_submit_clicked', {
            priceId: priceId,
            paymentMethod: 'stripe_card',
            hasEmail: !!email
        });

        setIsLoading(true)
        setMessage("")

        const cardElement = elements.getElement(CardElement)
        if (!cardElement) {
            setMessage(t('payment.errorCardElementNotFound'))
            setIsLoading(false)
            return
        }

        try {
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: "card",
                card: cardElement,
                billing_details: { email },
            })

            if (error) {
                throw new Error(error.message)
            }

            const token = localStorage.getItem('token')
            if (!token) {
                throw new Error(t('payment.errorAuthTokenNotFound'))
            }

            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/stripe/purchase-credits`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    email,
                    paymentMethodId: paymentMethod.id,
                    priceId,
                }),
            })

            const data = await response.json()

            if (!response.ok) {
                console.error('Subscription error:', data);
                const errorMessage = data.error?.includes('price')
                    ? t('payment.errorInvalidPriceIdDetail', { priceId })
                    : (data.error || t('payment.errorUnknown'));

                track('stripe_payment_failed', {
                    priceId: priceId,
                    paymentMethod: 'stripe_card',
                    error: data.error,
                    errorType: data.error?.includes('price') ? 'invalid_price' : 'payment_error'
                });

                throw new Error(errorMessage)
            }

            track('stripe_payment_success', {
                priceId: priceId,
                paymentMethod: 'stripe_card',
                paymentMethodId: paymentMethod.id
            });

            setMessage(t('payment.successPayment'))
            cardElement.clear()

            onSuccess()
        } catch (error) {
            console.error('Full error:', error);

            track('stripe_payment_error', {
                priceId: priceId,
                paymentMethod: 'stripe_card',
                error: error instanceof Error ? error.message : 'unknown',
                errorType: 'exception'
            });

            setMessage(`${t('payment.errorPrefix')}${error instanceof Error ? error.message : t('payment.errorUnknown')}`)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Box sx={{ p: 0, pt: 0, maxWidth: 600, mx: 'auto' }}>
            <form onSubmit={handleSubmit}>
                <Box sx={{ mt: 4 }}>
                    <Typography sx={{ mb: 1 }}>{t('payment.emailLabel')}</Typography>
                    <TextField
                        fullWidth
                        type="email"
                        placeholder={t('payment.emailPlaceholder')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading || !!user?.username}
                        required
                        sx={{ mb: 3 }}
                    />

                    <Typography sx={{ mb: 1 }}>{t('payment.cardDetailsLabel')}</Typography>
                    <Box
                        sx={{
                            mb: 4,
                            p: 2,
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: 1,
                            '&:hover': {
                                borderColor: 'rgba(0, 0, 0, 0.87)'
                            }
                        }}
                    >
                        <CardElement
                            options={{
                                style: {
                                    base: {
                                        fontSize: '12px',
                                        fontFamily: theme.typography.fontFamily,
                                        color: theme.palette.text.primary,
                                        '::placeholder': {
                                            color: theme.palette.text.secondary,
                                        },
                                        lineHeight: '26px',

                                    },
                                    invalid: {
                                        color: theme.palette.error.main,
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!stripe || isLoading}
                        fullWidth
                        sx={{
                            color: isLoading ? 'rgba(255, 255, 255, 0.5)' : undefined
                        }}
                    >
                        {isLoading ? t('payment.processing') : t('payment.buyNow')}
                    </Button>

                    {message && (
                        <Typography
                            sx={{
                                mt: 2,
                                textAlign: 'center',
                                color: message.includes("Error")
                                    ? theme.palette.error.main
                                    : theme.palette.success.main
                            }}
                        >
                            {message}
                        </Typography>
                    )}
                </Box>
            </form>
        </Box>
    )
}

const SubscriptionScreen: React.FC<SubscriptionScreenProps> = ({ priceId, onSuccess }) => {
    return (
        <ThemeProvider theme={theme}>
            <Elements stripe={stripePromise}>
                <SubscriptionForm priceId={priceId} onSuccess={onSuccess} />
            </Elements>
        </ThemeProvider>
    )
}

export default SubscriptionScreen
